import { replace } from 'react-router';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
  CrewClaim,
  crewClaimContext,
} from '~/app/features/crew/crewClaimMiddleware';
import {
  CrewOrder,
  crewOrderContext,
} from '~/app/features/order-middleware/orderMiddleware';
import { Settings, settingsContext } from '~/app/settingsMiddleware';
import Payments from '~/stores/payments';
import api from '~/utils/api';
import {
  pendingClaimLineItemToCalculatedLineItem,
  pendingClaimsToClaimLineItemCreate,
} from '~/utils/claimLineItem';
import { ClaimType } from '~/utils/constants';
import { getDeviceFingerprint } from '~/utils/fingerprint';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { extractSectionSummaryLabel } from '~/utils/summary';
import { AsyncReturnType, SetNonNullable } from '~/utils/types';

type Context = {
  settings: Settings;
  order: CrewOrder;
  crewClaim: SetNonNullable<CrewClaim, 'customerInfo'>;
};
type CalculationsResponse = AsyncReturnType<typeof getClaimCalculations>;

type Payment = NonNullable<CalculationsResponse['paymentIntent']> & {
  label: string;
};
const paymentSchema: z.ZodSchema<Payment> = z.object({
  clientSecret: z.string(),
  idFromPlatform: z.string(),
  amount: z.number(),
  label: z.string(),
});

const getClaimCalculations = ({
  order,
  settings,
  crewClaim: { claimLineItems },
}: Context) =>
  api.calculateBeforeClaimSubmission({
    params: { storefrontId: settings.storefrontId },
    body: {
      id: order.id,
      claimType: ClaimType.warranty,
      calculatedLineItems: claimLineItems.map((claim) =>
        pendingClaimLineItemToCalculatedLineItem(claim),
      ),
    },
  });

const setPaymentInformation = ({
  paymentIntent,
  deductions,
}: CalculationsResponse) => {
  if (!paymentIntent) {
    return { payment: undefined };
  }

  Payments.initializePayment(
    paymentIntent.clientSecret,
    paymentIntent.idFromPlatform,
  );

  return {
    payment: {
      ...paymentIntent,
      label: extractSectionSummaryLabel('FEE', deductions.sections),
    },
  };
};

const formatPageData =
  (context: Context) =>
  ({ payment }: ReturnType<typeof setPaymentInformation>) => ({
    payment,
    claims: context.crewClaim.claimLineItems,
  });

export const { loader, useLoaderData } = makeLoader(
  async ({ params, context }) => {
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);
    const crewClaim = context.get(crewClaimContext);

    const { customerInfo } = crewClaim;
    if (crewClaim.status !== 'new' || customerInfo === null) {
      return replace(`/${params.store}/order/${params.idFromPlatform}`);
    }

    return getClaimCalculations({
      settings,
      order,
      crewClaim: { ...crewClaim, customerInfo },
    })
      .then(setPaymentInformation)
      .then(
        formatPageData({
          settings,
          order,
          crewClaim: { ...crewClaim, customerInfo },
        }),
      );
  },
);

const confirmPayment = (payment?: Payment) =>
  payment
    ? Payments.confirmPayment().then((intentId) => ({
        ...payment,
        intentId,
      }))
    : Promise.resolve(null);

const createClaim =
  (
    context: Context,
    deviceFingerprint: { visitorId: string; requestId: string } | undefined,
  ) =>
  (confirmedPayment: AsyncReturnType<typeof confirmPayment>) => {
    const {
      settings: { storefrontId, stripeConfig },
      order: { id, currencyCode },
      crewClaim: { claimLineItems, customerInfo },
    } = context;

    const address = {
      ...customerInfo.address,
      firstName: customerInfo.firstName,
      lastName: customerInfo.lastName,
    };

    const customerPayment =
      !!confirmedPayment?.intentId &&
      !!confirmedPayment.amount &&
      !!stripeConfig
        ? {
            idFromPlatform: confirmedPayment.intentId,
            currencyCode,
            stripeConfig,
            //! amount should be in dollars or the ratio equivalent of the currency
            amount: confirmedPayment.amount,
          }
        : undefined;

    return api.createCrewClaim({
      params: { storefrontId },
      body: {
        feeName: confirmedPayment?.label,
        originalStoreOrderId: id,
        fingerprint: deviceFingerprint,
        billingAddress: address,
        shippingAddress: address,
        customerEmail: customerInfo.email,
        claimType: ClaimType.warranty,
        claimLineItems: pendingClaimsToClaimLineItemCreate(claimLineItems),
        giftCardIncentiveAmountApplied: 0,
        feeApplied: 0,
        returnShippingAmountApplied: 0,
        exchangeOrderShippingAmountApplied: 0,
        source: 'Customer_App',
        customerPayment,
      },
    });
  };

const formDataSchema = zfd.formData({
  payment: zfd.json(paymentSchema.optional()),
});

export const { action, useActionResult } = makeAction(
  async ({ params: { store }, formData, context }, appStore) => {
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);
    const crewClaim = context.get(crewClaimContext);
    const fingerprint = await getDeviceFingerprint(order.email);

    const result = formDataSchema.safeParse(formData);

    if (!result.success) {
      throw new Error('Invalid Warranty review form data.', {
        cause: result.error,
      });
    }
    const { customerInfo } = crewClaim;

    if (!customerInfo) {
      throw new Error('Customer information and address is not set.');
    }

    return confirmPayment(result.data.payment)
      .then((paymentId) => {
        appStore.dispatch(appStore.actions.crewClaim.submitClaim());

        return createClaim(
          { settings, order, crewClaim: { ...crewClaim, customerInfo } },
          fingerprint,
        )(paymentId).catch((err) => {
          appStore.dispatch(appStore.actions.crewClaim.unlockClaim());

          throw err;
        });
      })
      .then((claim) => {
        appStore.dispatch(appStore.actions.completeCrewClaim());

        return redirect(`/${store}/claim/overview/${claim.externalId}`);
      })
      .catch((err) => ({
        ok: false,
        message: err instanceof Error ? err.message : `${err}`,
      }));
  },
);
